import { CheckCircle2, IndianRupee, Clock, TrendingUp, Calendar, AlertCircle, ArrowLeft, Download, Building2, User, FileText, ShieldCheck, X, Mail } from "lucide-react";
import { formatCurrency } from "../../../../Component/AdminComponents/formatCurrency";
import { KpiCard } from "../../../../Component/AdminComponents/KpiCard";
import { SectionCard } from "../../../../Component/AdminComponents/SectionCard";
import { Badge } from "../../../../Component/AdminComponents/Badge";
import { useState, useEffect } from "react";
import BlueButton from "../../../../Component/Common/BlueButton";
import SearchInput from "../../../../Component/Common/SearchInput";
import { useNavigate } from "react-router-dom";
import { TabButton } from "./TabButton";
import { StatCard } from "./StatCard";
import { StepIndicator } from "./StepIndicator";
import { VerifyModal } from "./VerifyModal";
import { PayoutSummary } from "./PayoutSummary";
import { CommissionTable } from "./CommissionTable";
import { BASE_URL } from "../../../../config/api";
import axios from "axios"; 
import RegularLoader from "../../../../Component/Loader/RegularLoader";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const todayISO = () => new Date().toISOString().slice(0, 10);
const currentMonth = new Date().toISOString().slice(0, 7);

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT: CommissionsView
// ─────────────────────────────────────────────────────────────────────────────
export default function CommissionsView() {
  const [payoutList, setPayoutList]       = useState([]);
  const [activeTab, setActiveTab]         = useState("current");
  const [typeFilter, setTypeFilter]       = useState("ALL");
  const [selectedSummary, setSelectedSummary] = useState(null);

  // new states
  const [currentList, setCurrentList] = useState([]);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [currentSummary, setCurrentSummary] = useState({});
  const [currentTotalCount, setCurrentTotalCount] = useState(0);

  const [unbilledApiList, setUnbilledApiList] = useState([]);
  const [loadingUnbilled, setLoadingUnbilled] = useState(false);
  const [unbilledSummary, setUnbilledSummary] = useState({});

  const [billedApiList, setBilledApiList] = useState([]);
  const [loadingBilled, setLoadingBilled] = useState(false);
  const [billedSummary, setBilledSummary] = useState({});
  const [billedTotalCount, setBilledTotalCount] = useState(0);

  const getRoleFilter = (filter) => {
  if (filter === "MCP") return 2;
  if (filter === "CP") return 4;
  if (filter === "Promoter") return 6;
  return "all";
};

  const FILTERS = ["ALL", "MCP", "CP", "Promoter"];
  const Navigate = useNavigate();


  useEffect(() => {
  if (activeTab !== "current") return;

  const fetchCurrentPayouts = async () => {
    try {
      setLoadingCurrent(true);

      const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

      const res = await axios.get(
        `${BASE_URL}warranty/payouts/current-month`,
        {
          params: {
            entity_id: user?.id,
            role: user?.role,
          },
        }
      );

      const apiData = res?.data?.data?.data || [];
      const summary = res?.data?.summary || {};
      const totalCount = res?.data?.data?.total || 0; // ✅ IMPORTANT

      // 🔥 map API → UI format
      const formatted = apiData.map((item) => ({
        id: item.id,
        code: item.payout_code,
        entityName: item.entity_name || "—",
        forType: item.role_label,
        role: item.role,          // ✅ numeric role
        entity_id: item.entity_id,
        commissionAmount: Number(item.payout_amount),
        payableAmount: Number(item.payout_amount),
        transferred: item.status === "paid",
        date: item.start_date,
        end_date:item.end_date,
      }));

      setCurrentList(formatted);
      setCurrentSummary(summary);
      setCurrentTotalCount(totalCount);

    } catch (err) {
      console.error("Current payout API error", err);
    } finally {
      setLoadingCurrent(false);
    }
  };

  fetchCurrentPayouts();
}, [activeTab]);


useEffect(() => {
  if (activeTab !== "unbilled") return;

  const fetchUnbilled = async () => {
    try {
      setLoadingUnbilled(true);

      const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

      const params = {
        role: user?.role,
        role_filter: getRoleFilter(typeFilter),
      };

      // 🔥 role-based condition
      if (user?.role === 2 || user?.role === 4) {
        params.entity_id = user?.id;
      }

      const res = await axios.get(
        `${BASE_URL}warranty/payout-unbilled`,
        { params }
      );

      const apiData = res?.data?.data?.data || [];
      const summary = res?.data?.summary || {}; // ✅ ADD THIS

      // 🔥 map API → existing UI (NO UI CHANGE)
      const formatted = apiData.map((item) => ({
        id: item.id,
        entityName: item.entity_name || "—",
        forType: item.role_label,
        role: item.role,
        entity_id: item.entity_id,
        commissionAmount: Number(item.payout_amount),
        payableAmount: Number(item.payout_amount),
        transferred: false,
        date: item.start_date,
        end_date: item.end_date,

        // ✅ ADD THIS
        payout_request: item.payout_request || null,
      }));

      setUnbilledApiList(formatted);
      setUnbilledSummary(summary); // ✅ ADD THIS

    } catch (err) {
      console.error("Unbilled API error", err);
    } finally {
      setLoadingUnbilled(false);
    }
  };

  fetchUnbilled();
}, [activeTab, typeFilter]);


useEffect(() => {
  if (activeTab !== "billed") return;

  const fetchBilled = async () => {
    try {
      setLoadingBilled(true);

      const user = JSON.parse(
        localStorage.getItem("authUserData") || "{}"
      );

      const params = {};

      // ✅ ROLE 1
      if (user?.role === 1) {
        params.role_filter = getRoleFilter(typeFilter);
      }

      // ✅ ROLE 2 / 4 / 6
      if ([2, 4, 6].includes(user?.role)) {
        params.role = user?.role;
        params.entity_id = user?.id;
      }

      const res = await axios.get(
        `${BASE_URL}warranty/payout-billed`,
        { params }
      );

      const apiData = res?.data?.data?.data || [];
      const summary = res?.data?.summary || {};
      const totalCount = res?.data?.data?.total || 0;

      // ✅ MAP API DATA
      const formatted = apiData.map((item) => ({
        id: item.id,
        code: item.payout_code,
        entityName: item.entity_name || "—",
        forType: item.role_label,
        role: item.role,
        entity_id: item.entity_id,

        commissionAmount: Number(item.payout_amount || 0),
        payableAmount: Number(item.payout_amount || 0),

        transferred: true,

        date: item.start_date,
        end_date: item.end_date,

        bankName: item.bank_name || "—",
        accountLast4: item.account_last4 || "----",
        txnId: item.transaction_id || "-",
      }));

      setBilledApiList(formatted);
      setBilledSummary(summary);
      setBilledTotalCount(totalCount);

    } catch (err) {
      console.error("Billed API error", err);
    } finally {
      setLoadingBilled(false);
    }
  };

  fetchBilled();
}, [activeTab, typeFilter]);

  const billedList = billedApiList;
  const unbilledList = unbilledApiList;
  const activeList =
    activeTab === "current"
      ? currentList
      : activeTab === "billed"
      ? billedList
      : unbilledApiList;

  const isThisMonth = (p) => p.date?.startsWith(currentMonth);

  const totalBilled =
    Number(billedSummary?.total_billed_amount || 0);

  const monthlyBilled =
    Number(billedSummary?.monthly_billed || 0);

  const totalBilledCount = billedTotalCount;

  const totalUnbilled =
    activeTab === "current"
      ? Number(currentSummary?.total_unbilled_amount || 0)
      : Number(unbilledSummary?.total_unbilled || 0);
  const monthlyUnbilled =
    activeTab === "current"
      ? Number(currentSummary?.total_unbilled_amount || 0)
      : Number(unbilledSummary?.monthly_unbilled || 0);

 const currentTabCount = currentTotalCount;

  const unbilledTabCount = unbilledApiList.length;

  const billedTabCount = billedTotalCount;

  const avgUnbilled =
    activeTab === "unbilled"
      ? Number(unbilledSummary?.average_payout || 0)
      : unbilledTabCount
      ? Math.round(totalUnbilled / unbilledTabCount)
      : 0;


  const billedCards = [
    { label: "Total Billed",      value: `₹${formatCurrency(totalBilled)}`,      sublabel: `${totalBilledCount} payouts transferred`, icon: CheckCircle2, accent: "#059669", bg: "#ECFDF5" },
    { label: "Monthly Total Bill", value: `₹${formatCurrency(monthlyBilled)}`,   sublabel: "Transferred this month",                   icon: Calendar,     accent: "#2563EB", bg: "#EFF6FF" },
    { label: "Avg. Per Payout",   value: `₹${formatCurrency(totalBilledCount ? Math.round(totalBilled / totalBilledCount) : 0)}`, sublabel: "Average transferred amount", icon: TrendingUp, accent: "#7C3AED", bg: "#F5F3FF" },
  ];

  const unbilledCards = [
    { label: "Total Unbill",        value: `₹${formatCurrency(totalUnbilled)}`,    sublabel: `${unbilledTabCount} payouts pending`, icon: Clock,        accent: "#D97706", bg: "#FFFBEB" },
    { label: "Monthly Total Unbill", value: `₹${formatCurrency(monthlyUnbilled)}`, sublabel: "Pending this month",                   icon: Calendar,     accent: "#EF4444", bg: "#FEF2F2" },
    { label: "Avg. Per Payout",     value: `₹${formatCurrency(avgUnbilled)}`, sublabel: "Average pending amount", icon: AlertCircle, accent: "#0891B2", bg: "#ECFEFF" },
  ];



  const activeCards = activeTab === "billed" ? billedCards : unbilledCards;

  const handleSummaryClick = async (row) => {
  try {
    const res = await axios.get(
      `${BASE_URL}warranty/payout-statement`,
      {
        params: { payout_id: row.id },
      }
    );

    // ✅ store API response
    setSelectedSummary({
      ...res?.data?.statement,
      payout_id: row.id, // ✅ attach id
    });
  } catch (err) {
    console.error("Statement API error", err);
  }
};


  if (selectedSummary) {
    return (
      <PayoutSummary
        data={selectedSummary}
        onBack={() => setSelectedSummary(null)}
        onVerified={() => {
          setSelectedSummary(null);
          setActiveTab("billed");
        }}
      />
    );
  }

  return (
    <div className="space-y-5">

      {/* Tab Bar */}
      <div className="flex items-center" style={{ borderBottom: "2px solid #F1F5F9" }}>
        <TabButton
          label="Current Payout"
          active={activeTab === "current"}
          count={currentTabCount}
          onClick={() => setActiveTab("current")}
          accent="#059669"
        />

        <TabButton
          label="Unbilled"
          active={activeTab === "unbilled"}
          count={unbilledTabCount}
          onClick={() => setActiveTab("unbilled")}
          accent="#D97706"
        />

        <TabButton
          label="Billed"
          active={activeTab === "billed"}
          count={billedTabCount}
          onClick={() => setActiveTab("billed")}
          accent="#059669"
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {activeCards.map((card, i) => (
          <StatCard key={card.label} {...card} index={i} />
        ))}
      </div>

      {/* Table */}
      <SectionCard
        title={activeTab === "billed" ? "Billed Payouts Register" : activeTab === "unbilled" ? "Unbilled Payouts Register" : "Current Month"}
        right={
          <div className="flex justify-end gap-3 mb-5">
            <div className="flex gap-2 flex-wrap">
              {FILTERS.map((item) => (
                <button
                  key={item}
                  onClick={() => setTypeFilter(item)}
                  className="px-4 py-1.5 rounded-full text-xs font-bold transition-all"
                  style={{
                    background: typeFilter === item ? "#2563EB" : "#F1F5F9",
                    color:      typeFilter === item ? "#FFFFFF" : "#64748B",
                    border:     typeFilter === item ? "1px solid #2563EB" : "1px solid #E2E8F0",
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
            <SearchInput />
            <div>
              <span
                className="text-[11px] font-bold px-2.5 py-1 rounded-full"
                style={{
                  background: activeTab === "billed" ? "#ECFDF5" : "#FFFBEB",
                  color:      activeTab === "billed" ? "#059669" : "#D97706",
                }}
              >
                {activeList.length} records
              </span>
            </div>
          </div>
        }
      >
        {(activeTab === "current" && loadingCurrent) ||
          (activeTab === "unbilled" && loadingUnbilled) ||
          (activeTab === "billed" && loadingBilled) ? (
          <div className="py-10 text-center text-slate-400 text-sm font-semibold">
            <RegularLoader />
          </div>
        ) : (
          <CommissionTable
            list={activeList}
            onToggle={() => {}}
            activeTab={activeTab}
            Navigate={Navigate}
            setSelectedSummary={handleSummaryClick}
          />
        )}
      </SectionCard>
    </div>
  );
}


// import { CheckCircle2, IndianRupee, Clock, TrendingUp, Calendar, AlertCircle, ArrowLeft, Download, Building2, User, FileText, ShieldCheck, X, Mail } from "lucide-react";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { useState, useMemo, useRef } from "react";
// import BlueButton from "../../../Component/Common/BlueButton";
// import SearchInput from "../../../Component/Common/SearchInput";
// import { useNavigate } from "react-router-dom";


// // ─── MOCK DATA ────────────────────────────────────────────────────────────────
// const commissionPayouts = [
//   {
//     id: "PAY-9001",
//     forType: "MCP",
//     entityName: "Rahul Patil",
//     period: "Week 42 · 2025",
//     role: "Onboarding Commission",
//     commissionAmount: 22000,
//     payableAmount: 20000,
//     transferred: true,
//     bankName: "HDFC Bank",
//     accountLast4: "1234",
//     txnId: "TXN-HDFC-2025-001",
//     date: "2025-10-25",
//   },
//   {
//     id: "PAY-9002",
//     forType: "MCP",
//     entityName: "Shree Mobile Store",
//     period: "Week 42 · 2025",
//     role: "Warranty Sales Payout",
//     commissionAmount: 12000,
//     payableAmount: 12000,
//     transferred: false,
//     bankName: "ICICI Bank",
//     accountLast4: "5678",
//     txnId: "-",
//     date: "2025-10-27",
//   },
//   {
//     id: "PAY-9003",
//     forType: "CP",
//     entityName: "Priya Singh",
//     period: "Week 41 · 2025",
//     role: "Warranty Sales Payout",
//     commissionAmount: 15000,
//     payableAmount: 15000,
//     transferred: true,
//     bankName: "SBI",
//     accountLast4: "9999",
//     txnId: "TXN-SBI-2025-221",
//     date: "2025-10-20",
//   },
//   {
//     id: "PAY-9004",
//     forType: "Promoter",
//     entityName: "A1 Electronics",
//     period: "Week 43 · 2025",
//     role: "Warranty Sales Payout",
//     commissionAmount: 8500,
//     payableAmount: 8500,
//     transferred: false,
//     bankName: "Axis Bank",
//     accountLast4: "3421",
//     txnId: "-",
//     date: "2025-11-01",
//   },
//   {
//     id: "PAY-9005",
//     forType: "CP",
//     entityName: "Kiran Desai",
//     period: "Week 43 · 2025",
//     role: "Warranty Sales Payout",
//     commissionAmount: 18000,
//     payableAmount: 17000,
//     transferred: false,
//     bankName: "Kotak Bank",
//     accountLast4: "7812",
//     txnId: "-",
//     date: "2025-11-02",
//   },
// ];

// // ─── HELPERS ──────────────────────────────────────────────────────────────────
// const todayISO = () => new Date().toISOString().slice(0, 10);
// const currentMonth = new Date().toISOString().slice(0, 7); // "2025-11"

// // ─────────────────────────────────────────────────────────────────────────────
// //  ATOM: TabButton
// // ─────────────────────────────────────────────────────────────────────────────
// function TabButton({ label, active, count, onClick, accent }) {
//   return (
//     <button
//       onClick={onClick}
//       className="relative flex items-center gap-2 px-5 py-3 text-[13px] font-bold
//         transition-all duration-150 border-b-2 focus:outline-none"
//       style={{
//         borderBottomColor: active ? accent : "transparent",
//         color:             active ? accent : "#94A3B8",
//         background:        "transparent",
//       }}
//     >
//       {label}
//       {count !== undefined && (
//         <span
//           className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black"
//           style={{
//             background: active ? accent : "#F1F5F9",
//             color:      active ? "#fff"  : "#64748B",
//           }}
//         >
//           {count}
//         </span>
//       )}
//     </button>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  MOLECULE: StatCard  — matching reference image style
// // ─────────────────────────────────────────────────────────────────────────────

// function StatCard({ label, value, sublabel, icon: Icon, accent, bg, index }) {
//   return (
//     <div
//       className="rounded-2xl p-5 flex justify-between gap-3 animate-fade-in-up"
//       style={{
//         background: "#FFFFFF",
//         border:     `1px solid ${bg}`,
//         boxShadow:  "0 2px 12px rgba(0,0,0,0.05)",
//         animationDelay: `${index * 0.06}s`,
//       }}
//     >
      

//       {/* Value */}
//       <div>
//         <p
//           className="text-[20px] font-black leading-none mb-1"
//           style={{ color: accent, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
//         >
//           {value}
//         </p>
//         <p className="text-[12px] font-bold text-slate-600">{label}</p>
//         {sublabel && (
//           <p className="text-[11px] text-slate-400 mt-0.5">{sublabel}</p>
//         )}
//       </div>

//       {/* Icon */}
//       <div
//         className="w-10 h-10 rounded-xl flex items-center justify-center"
//         style={{ background: bg }}
//       >
//         <Icon size={18} style={{ color: accent }}/>
//       </div>
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  MOLECULE: CommissionTable  — shared by both tabs
// // ─────────────────────────────────────────────────────────────────────────────

// function CommissionTable({ list, onToggle,activeTab, Navigate, setSelectedSummary }) {
//   if (!list.length) {
//     return (
//       <div className="flex flex-col items-center justify-center py-12 text-slate-400">
//         <AlertCircle size={28} className="mb-2 opacity-40"/>
//         <p className="text-sm font-semibold">No records found</p>
//       </div>
//     );
//   }

//   return (
//     <div className="overflow-x-auto">
//       <table className="min-w-full text-xs">
//         <thead>
//           <tr
//             className="text-left"
//             style={{ borderBottom:"1px solid #F1F5F9" }}
//           >
//            {[
//               "Unbilled ID",
//               "Entity",
//               "Role / Period",
//               "Amounts (₹)",
//               ...(activeTab === "billed" ? ["Bank / Txn", "Date"] : []),
//               "Status",
//               "Action",
//             ].map(h => (
//               <th
//                 key={h}
//                 className="pb-3 pr-4 font-black uppercase tracking-widest"
//                 style={{ fontSize:10, color:"#94A3B8" }}
//               >
//                 {h}
//               </th>
//             ))}
//           </tr>
//         </thead>
//         <tbody>
//           {list.map((p, i) => (
//             <tr
//               key={p.id}
//               className="group hover:bg-slate-50/60 transition-colors"
//               style={{ borderBottom:"1px solid #F8FAFC" }}
//             >
//               {/* ID */}
//               <td className="py-3 pr-4">
//                 <span className="font-bold text-slate-700">{p.id}</span>
//               </td>

//               {/* Entity */}
//               <td className="py-3 pr-4">
//                 <p className="font-bold text-slate-800 whitespace-nowrap">{p.entityName}</p>
//                 <p className="text-[10px] text-slate-400 mt-0.5">{p.forType}</p>
//               </td>

//               {/* Role / Period */}
//               <td className="py-3 pr-4">
//                 <p className="text-slate-700 font-semibold whitespace-nowrap">{p.role}</p>
//                 <p className="text-[10px] text-slate-400 mt-0.5">{p.date} to {p.date}</p>
//               </td>

//               {/* Amounts */}
//               <td className="py-3 pr-4">
//                 <p className="font-semibold text-slate-700">₹{formatCurrency(p.commissionAmount)}</p>
//                 {/* <p className="text-[10px] text-slate-400 mt-0.5">
//                   Payable: ₹{formatCurrency(p.payableAmount)}
//                 </p> */}
//               </td>

//               {/* Bank / Txn */}
//               { activeTab === "billed" && (
//                 <td className="py-3 pr-4">
//                 <p className="font-semibold text-slate-700 whitespace-nowrap">
//                   {p.bankName} · ****{p.accountLast4}
//                 </p>
//                 <p className="text-[10px] text-slate-400 mt-0.5">Txn: {p.txnId}</p>
//               </td>

//               )}
//               { activeTab === "billed" && (
//               <td className="py-3 pr-4">
//                 <p className="text-slate-600 whitespace-nowrap">{p.date}</p>
//               </td>
//               )}

//               {/* Status */}
//               <td className="py-3 pr-4">
//                 <span
//                   className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
//                   style={{
//                     background: p.transferred ? "#ECFDF5" : "#FFFBEB",
//                     color:      p.transferred ? "#059669" : "#D97706",
//                     border:     `1px solid ${p.transferred ? "#A7F3D0" : "#FDE68A"}`,
//                   }}
//                 >
//                   <span
//                     className="w-1.5 h-1.5 rounded-full"
//                     style={{ background: p.transferred ? "#10B981" : "#F59E0B" }}
//                   />
//                   {p.transferred ? "Transferred" : "Pending"}
//                 </span>
//               </td>

//               {/* Action */}
//               <td className="py-3 ">
//                 <div className="flex gap-2">
//                   { activeTab !== "current" && (
//                   <button
//                   onClick={() => onToggle(p.id)}
//                   className="px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
//                   style={{
//                     background:  p.transferred ? "#FEF2F2" : "#EFF6FF",
//                     color:       p.transferred ? "#EF4444" : "#2563EB",
//                     border:      `1px solid ${p.transferred ? "#FECACA" : "#BFDBFE"}`,
//                   }}
//                 >
//                   {p.transferred ? "Mark Pending" : "Mark Transferred"}
//                 </button>
//                   )}

//                   {activeTab === "unbilled" && (
//                     <button
//                       onClick={() => setSelectedSummary(p)}
//                       className="px-3 py-1.5 rounded-xl text-[11px] font-bold bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100"
//                     >
//                       Summary
//                     </button>
//                   )}
//                 <BlueButton onClick={() =>
//                         Navigate(`/commission-details`)
//                       }>
//                   View
//                 </BlueButton>
//                 </div>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// }

// // function PayoutSummary({ data, onBack }) {
// //   const PRODUCTS = [
// //     { name: "Extended Warranty", hsn: "999523", mrp: 500, units: 12, rate: 350 },
// //     { name: "Screen Damage Protection", hsn: "999524", mrp: 300, units: 8, rate: 220 },
// //     { name: "Total Protection Plan", hsn: "999525", mrp: 600, units: 5, rate: 480 },
// //   ];

// //   const GST_RATE = 0.09; // 9% CGST + 9% SGST
// //   const DISCOUNT = 120;

// //   const subtotal = PRODUCTS.reduce((acc, p) => acc + p.rate * p.units, 0);
// //   const cgst = subtotal * GST_RATE;
// //   const sgst = subtotal * GST_RATE;
// //   const total = subtotal - DISCOUNT;

// //   const formatNum = (n) => n.toLocaleString("en-IN", { minimumFractionDigits: 2 });

// //   return (
// //     <div className="space-y-4">
// //       {/* Action Bar */}
// //       <div className="flex items-center justify-between">
// //         <button
// //           onClick={onBack}
// //           className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
// //         >
// //           <ArrowLeft size={16} /> Back to Register
// //         </button>
// //         <button
// //           onClick={() => window.print()}
// //           className="flex items-center gap-2 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg"
// //         >
// //           <Download size={13} /> Download PDF
// //         </button>
// //       </div>

// //       {/* Invoice Card */}
// //       <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden text-xs">

// //         {/* Logo + Company */}
// //           <div className="space-y-1.5">
// //             <div className="bg-slate-900 p-8 text-white flex justify-between items-start">
// //             <div>
// //               <h2 className="text-2xl font-black tracking-tight italic">GOELECTRONIX</h2>
// //               <p className="text-slate-400 text-[11px] mt-1 uppercase tracking-widest font-bold">Official Commission Statement</p>
// //             </div>
// //             <div className="text-right">
// //               <p className="text-[11px] font-bold text-slate-400 uppercase">Statement ID</p>
// //               <p className="font-mono text-sm">{data.id.replace('PAY', 'STMT')}</p>
// //             </div>
// //           </div>

// //           <div />

// //           {/* Invoice Meta */}
// //           {/* <div className="text-right">
// //             <p className="text-2xl font-black text-slate-900 tracking-tight">Performa Invoice</p>
// //             <p className="text-md font-bold uppercase tracking-widest text-slate-400 mb-3">
// //               Official Commission Statement
// //             </p>
// //             <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-left text-[10px]">
// //               {[
// //                 ["Invoice #", data.id.replace("PAY", "INV")],
// //                 ["Invoice Date", data.date],
// //                 // ["Terms", "Credit 7 Days"],
// //                 // ["Due Date", "—"],
// //                 // ["Place of Supply", "Maharashtra (27)"],
// //               ].map(([lbl, val]) => (
// //                 <>
// //                   <span className="text-slate-400 font-semibold">{lbl}</span>
// //                   <span className="font-bold text-slate-800 font-mono">{val}</span>
// //                 </>
// //               ))}
// //             </div>
// //           </div> */}
// //         </div>

// //         {/* ── BILL TO / SHIP TO ── */}
// //         <div className="grid grid-cols-2 border-b border-slate-200">
// //           {[
// //             {
// //               logo: User,
// //               label: "Entity Details",
// //               name: `${data.entityName}`,
// //               badge: data.forType,
// //               body: `Type: ${data.forType}\nBank: ${data.bankName} (****${data.accountLast4})\nPeriod: ${data.date} to ${data.date}\nGSTIN: 27AAACG0000A1Z5`,
// //             },
// //             {
// //               logo: Building2,
// //               label: "Company Details",
// //               name: "GoElectronix Solutions Pvt Ltd",
// //               badge: null,
// //               body: "Plot No. 42, Tech Park, Sector 11,\nNavi Mumbai, Maharashtra - 400706",
// //             },
// //           ].map((block, i) => (
// //             <div
// //               key={i}
// //               className={`p-4 ${i === 1 ? "border-l border-slate-200" : ""}`}
// //             >
// //               <p className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-600 mb-1.5">
// //                 {block.logo && <block.logo className="w-3 h-3" />}
// //                 {block.label}
// //               </p>
// //               <p className="font-bold text-slate-900 text-[11px] mb-1">
// //                 {block.name}
// //                 {block.badge && (
// //                   <span className="ml-2 text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
// //                     {block.badge}
// //                   </span>
// //                 )}
// //               </p>
// //               <p className="text- text-slate-500 leading-relaxed whitespace-pre-line">
// //                 {block.body}
// //               </p>
// //             </div>
// //           ))}
// //         </div>

// //         {/* ── PRODUCT TABLE ── */}
// //         <table className="w-full text-[10px]">
// //           <thead>
// //             <tr className="bg-slate-50 border-b border-slate-200">
// //               <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500 w-6">#</th>
// //               <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500">Item & Description</th>
// //               <th className="py-2 px-3 text-[9px] font-black uppercase tracking-wider text-slate-500">HSN/SAC</th>
// //               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">MRP</th>
// //               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">Qty</th>
// //               <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-500">Rate</th>
// //               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-blue-50/40" colSpan={2}>CGST</th>
// //               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-indigo-50/40" colSpan={2}>SGST</th>
// //               <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-700">Amount</th>
// //             </tr>
// //             <tr className="bg-slate-50 border-b border-slate-200">
// //               {["","","","","","","%","Amt","%","Amt",""].map((h, i) => (
// //                 <th key={i} className="py-1 px-3 text-[9px] font-bold text-slate-400 text-center">{h}</th>
// //               ))}
// //             </tr>
// //           </thead>
// //           <tbody>
// //             {PRODUCTS.map((p, i) => {
// //               const lineTotal = p.rate * p.units;
// //               const lineCgst = lineTotal * GST_RATE;
// //               return (
// //                 <tr key={p.name} className="border-b border-slate-100 hover:bg-slate-50/50">
// //                   <td className="py-3 px-3 text-slate-500">{i + 1}</td>
// //                   <td className="py-3 px-3">
// //                     <p className="font-bold text-slate-800">{p.name}</p>
// //                     <p className="text-[9px] text-slate-400">Warranty plan for Android smartphones</p>
// //                   </td>
// //                   <td className="py-3 px-3 font-mono text-slate-600">{p.hsn}</td>
// //                   <td className="py-3 px-3 text-center font-mono">{p.mrp}</td>
// //                   <td className="py-3 px-3 text-center font-mono">{p.units} pcs</td>
// //                   <td className="py-3 px-3 text-right font-mono">{p.rate}.00</td>
// //                   <td className="py-3 px-3 text-center font-mono bg-blue-50/20">9%</td>
// //                   <td className="py-3 px-3 text-center font-mono bg-blue-50/20">{formatNum(lineCgst)}</td>
// //                   <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">9%</td>
// //                   <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">{formatNum(lineCgst)}</td>
// //                   <td className="py-3 px-3 text-right font-mono font-bold text-slate-800">
// //                     {formatNum(lineTotal)}
// //                   </td>
// //                 </tr>
// //               );
// //             })}
// //           </tbody>
// //         </table>

// //         {/* ── TOTALS ROW ── */}
// //         <div className="grid border-t border-slate-200" style={{ gridTemplateColumns: "1fr 280px" }}>
// //           <div className="p-4 border-r border-slate-200">
// //             <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Total In Words</p>
// //             <p className="font-bold text-slate-800 text-[11px]">Indian Rupee Ten Thousand Only</p>
// //           </div>
// //           <div className="text-[10px]">
// //             {[
// //               { label: "Sub Total (Tax Inclusive)", val: formatNum(subtotal), cls: "bg-slate-50" },
// //               { label: "CGST (9%)", val: formatNum(cgst), cls: "" },
// //               { label: "SGST (9%)", val: formatNum(sgst), cls: "" },
// //               // { label: "Discount", val: `(-) ${formatNum(DISCOUNT)}`, cls: "text-red-500" },
// //             ].map(({ label, val, cls }) => (
// //               <div
// //                 key={label}
// //                 className={`flex justify-between px-5 py-2 border-b border-slate-100 ${cls}`}
// //               >
// //                 <span className="font-semibold text-slate-600">{label}</span>
// //                 <span className="font-mono font-semibold">{val}</span>
// //               </div>
// //             ))}

// //             <div className="flex justify-between px-5 py-3 bg-slate-900">
// //               <span className="font-black text-white text-[13px]">Total</span>
// //               <span className="font-mono font-black text-blue-400 text-[13px]">₹{formatNum(total)}</span>
// //             </div>
// //           </div>
// //         </div>

// //         {/* ── PAYMENT DETAILS ── */}
// //         <div className="flex gap-5 p-4 border-t border-slate-200">
// //           <div className="w-16 h-16 border-2 border-slate-200 rounded flex items-center justify-center text-[8px] text-slate-400 font-bold bg-slate-50">
// //             QR Code
// //           </div>
// //           <div className="text-[10px] text-slate-500 leading-relaxed">
// //             <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Payment TO</p>
// //             <p className="font-bold text-slate-800">Shree Mobile Store</p>
// //             <p>HDFC BANK LTD · ACC NO.: 502 000 624 338 73</p>
// //             <p>IFSC CODE: HDFC0001452 · BRANCH: C.G ROAD CHEMBUR · ACC TYPE: CURRENT</p>
// //           </div>
// //         </div>

// //         {/* ── FOOTER BRAND ── */}
// //         <div className="flex justify-end px-5 py-3 border-t border-slate-200">
// //           <span className="font-black text-slate-900">Go<span className="text-blue-500">electronix</span></span>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // }

// function PayoutSummary({ data, onBack, onVerified }) {
//   const PRODUCTS = [
//     { name: "Extended Warranty", hsn: "999523", mrp: 500, units: 12, rate: 350 },
//     { name: "Screen Damage Protection", hsn: "999524", mrp: 300, units: 8, rate: 220 },
//     { name: "Total Protection Plan", hsn: "999525", mrp: 600, units: 5, rate: 480 },
//   ];

//   const GST_RATE = 0.09;
//   const DISCOUNT = 120;
//   const subtotal = PRODUCTS.reduce((acc, p) => acc + p.rate * p.units, 0);
//   const cgst = subtotal * GST_RATE;
//   const sgst = subtotal * GST_RATE;
//   const total = subtotal - DISCOUNT;
//   const formatNum = (n) => n.toLocaleString("en-IN", { minimumFractionDigits: 2 });

//   // ── Verification state ────────────────────────────────────────────────────
//   const [showModal, setShowModal]       = useState(false);
//   const [verifyStep, setVerifyStep]     = useState("invoice"); // invoice | sendOtp | verifyOtp | success
//   const [invoiceVal, setInvoiceVal]     = useState("");
//   const [invoiceError, setInvoiceError] = useState("");
//   const [isSending, setIsSending]       = useState(false);
//   const [otpDigits, setOtpDigits]       = useState(["","","","","",""]);
//   const [otpError, setOtpError]         = useState("");
//   const [redirectPct, setRedirectPct]   = useState(0);
//   const [redirectSecs, setRedirectSecs] = useState(3);
//   const otpRefs = [
//     useRef(null),
//     useRef(null),
//     useRef(null),
//     useRef(null),
//     useRef(null),
//     useRef(null),
//   ];

//   const openModal = () => {
//     setShowModal(true);
//     setVerifyStep("invoice");
//     setInvoiceVal("");
//     setInvoiceError("");
//     setOtpDigits(["","","","","",""]);
//     setOtpError("");
//     setIsSending(false);
//     setRedirectPct(0);
//     setRedirectSecs(3);
//   };

//   const closeModal = () => setShowModal(false);

//   // Step 1 → 2
//   const handleInvoiceNext = () => {
//     if (!invoiceVal.trim() || invoiceVal.trim().length < 5) {
//       setInvoiceError("Please enter a valid invoice number (e.g. INV-9001-2025)");
//       return;
//     }
//     setInvoiceError("");
//     setVerifyStep("sendOtp");
//   };

//   // Step 2 → 3
//   const handleSendOtp = () => {
//     setIsSending(true);
//     setTimeout(() => {
//       setIsSending(false);
//       setVerifyStep("verifyOtp");
//       setTimeout(() => otpRefs[0].current?.focus(), 100);
//     }, 2000);
//   };

//   // OTP digit input
//   const handleOtpInput = (index, val) => {
//     if (!/^\d*$/.test(val)) return;
//     const next = [...otpDigits];
//     next[index] = val.slice(-1);
//     setOtpDigits(next);
//     setOtpError("");
//     if (val && index < 5) otpRefs[index + 1].current?.focus();
//   };

//   const handleOtpKeydown = (e, index) => {
//     if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
//       otpRefs[index - 1].current?.focus();
//     }
//   };

//   // Step 3 → success
//   const handleVerifyOtp = () => {
//     const entered = otpDigits.join("");
//     if (entered !== "123456") {
//       setOtpError("Invalid OTP. Please try again. (Demo: use 123456)");
//       return;
//     }
//     setOtpError("");
//     setVerifyStep("success");
//     startRedirect();
//   };

//   const startRedirect = () => {
//     let pct = 0;
//     let secs = 3;
//     const interval = setInterval(() => {
//       pct += 100 / 30;
//       setRedirectPct(Math.min(pct, 100));
//       if (pct >= 33 && secs === 3) { secs = 2; setRedirectSecs(2); }
//       if (pct >= 66 && secs === 2) { secs = 1; setRedirectSecs(1); }
//       if (pct >= 100) {
//         clearInterval(interval);
//         setShowModal(false);
//         onVerified?.();
//       }
//     }, 100);
//   };

//   const resendOtp = () => {
//     setOtpDigits(["","","","","",""]);
//     setOtpError("");
//     setTimeout(() => otpRefs[0].current?.focus(), 50);
//   };

//   // ── Step indicator helper ─────────────────────────────────────────────────
//   const steps = ["Invoice", "Send OTP", "Verify OTP"];
//   const stepIndex = { invoice: 0, sendOtp: 1, verifyOtp: 2, success: 3 };
//   const currentStepIdx = stepIndex[verifyStep] ?? 0;

//   return (
//     <div className="space-y-4">
//       {/* Action Bar */}
//       <div className="flex items-center justify-between">
//         <button
//           onClick={onBack}
//           className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
//         >
//           <ArrowLeft size={16} /> Back to Register
//         </button>
//         <button
//           onClick={() => window.print()}
//           className="flex items-center gap-2 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg"
//         >
//           <Download size={13} /> Download PDF
//         </button>
//       </div>

//       {/* Invoice Card */}
//       <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden text-xs">

//         {/* Header */}
//         <div className="bg-slate-900 p-8 text-white flex justify-between items-start">
//           <div>
//             <h2 className="text-2xl font-black tracking-tight italic">GOELECTRONIX</h2>
//             <p className="text-slate-400 text-[11px] mt-1 uppercase tracking-widest font-bold">Official Commission Statement</p>
//           </div>
//           <div className="text-right">
//             <p className="text-[11px] font-bold text-slate-400 uppercase">Statement ID</p>
//             <p className="font-mono text-sm">{data.id.replace("PAY", "STMT")}</p>
//           </div>
//         </div>

//         {/* Bill To / Company */}
//         <div className="grid grid-cols-2 border-b border-slate-200">
//           {[
//             {
//               logo: User,
//               label: "Entity Details",
//               name: data.entityName,
//               badge: data.forType,
//               body: `Type: ${data.forType}\nBank: ${data.bankName} (****${data.accountLast4})\nPeriod: ${data.date} to ${data.date}\nGSTIN: 27AAACG0000A1Z5`,
//             },
//             {
//               logo: Building2,
//               label: "Company Details",
//               name: "GoElectronix Solutions Pvt Ltd",
//               badge: null,
//               body: "Plot No. 42, Tech Park, Sector 11,\nNavi Mumbai, Maharashtra - 400706",
//             },
//           ].map((block, i) => (
//             <div key={i} className={`p-4 ${i === 1 ? "border-l border-slate-200" : ""}`}>
//               <p className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-600 mb-1.5">
//                 {block.logo && <block.logo className="w-3 h-3" />}
//                 {block.label}
//               </p>
//               <p className="font-bold text-slate-900 text-[11px] mb-1">
//                 {block.name}
//                 {block.badge && (
//                   <span className="ml-2 text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
//                     {block.badge}
//                   </span>
//                 )}
//               </p>
//               <p className="text-slate-500 leading-relaxed whitespace-pre-line">{block.body}</p>
//             </div>
//           ))}
//         </div>

//         {/* Product Table */}
//         <table className="w-full text-[10px]">
//           <thead>
//             <tr className="bg-slate-50 border-b border-slate-200">
//               <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500 w-6">#</th>
//               <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500">Item & Description</th>
//               <th className="py-2 px-3 text-[9px] font-black uppercase tracking-wider text-slate-500">HSN/SAC</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">MRP</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">Qty</th>
//               <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-500">Rate</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-blue-50/40" colSpan={2}>CGST</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-indigo-50/40" colSpan={2}>SGST</th>
//               <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-700">Amount</th>
//             </tr>
//             <tr className="bg-slate-50 border-b border-slate-200">
//               {["","","","","","","%","Amt","%","Amt",""].map((h, i) => (
//                 <th key={i} className="py-1 px-3 text-[9px] font-bold text-slate-400 text-center">{h}</th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {PRODUCTS.map((p, i) => {
//               const lineTotal = p.rate * p.units;
//               const lineCgst  = lineTotal * GST_RATE;
//               return (
//                 <tr key={p.name} className="border-b border-slate-100 hover:bg-slate-50/50">
//                   <td className="py-3 px-3 text-slate-500">{i + 1}</td>
//                   <td className="py-3 px-3">
//                     <p className="font-bold text-slate-800">{p.name}</p>
//                     <p className="text-[9px] text-slate-400">Warranty plan for Android smartphones</p>
//                   </td>
//                   <td className="py-3 px-3 font-mono text-slate-600">{p.hsn}</td>
//                   <td className="py-3 px-3 text-center font-mono">{p.mrp}</td>
//                   <td className="py-3 px-3 text-center font-mono">{p.units} pcs</td>
//                   <td className="py-3 px-3 text-right font-mono">{p.rate}.00</td>
//                   <td className="py-3 px-3 text-center font-mono bg-blue-50/20">9%</td>
//                   <td className="py-3 px-3 text-center font-mono bg-blue-50/20">{formatNum(lineCgst)}</td>
//                   <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">9%</td>
//                   <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">{formatNum(lineCgst)}</td>
//                   <td className="py-3 px-3 text-right font-mono font-bold text-slate-800">{formatNum(lineTotal)}</td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>

//         {/* Totals */}
//         <div className="grid border-t border-slate-200" style={{ gridTemplateColumns: "1fr 280px" }}>
//           <div className="p-4 border-r border-slate-200">
//             <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Total In Words</p>
//             <p className="font-bold text-slate-800 text-[11px]">Indian Rupee Ten Thousand Only</p>
//           </div>
//           <div className="text-[10px]">
//             {[
//               { label: "Sub Total (Tax Inclusive)", val: formatNum(subtotal), cls: "bg-slate-50" },
//               { label: "CGST (9%)",                 val: formatNum(cgst),     cls: "" },
//               { label: "SGST (9%)",                 val: formatNum(sgst),     cls: "" },
//             ].map(({ label, val, cls }) => (
//               <div key={label} className={`flex justify-between px-5 py-2 border-b border-slate-100 ${cls}`}>
//                 <span className="font-semibold text-slate-600">{label}</span>
//                 <span className="font-mono font-semibold">{val}</span>
//               </div>
//             ))}
//             <div className="flex justify-between px-5 py-3 bg-slate-900">
//               <span className="font-black text-white text-[13px]">Total</span>
//               <span className="font-mono font-black text-blue-400 text-[13px]">₹{formatNum(total)}</span>
//             </div>
//           </div>
//         </div>

//         {/* Payment TO */}
//         <div className="flex gap-5 p-4 border-t border-slate-200">
//           <div className="w-16 h-16 border-2 border-slate-200 rounded flex items-center justify-center text-[8px] text-slate-400 font-bold bg-slate-50 flex-shrink-0">
//             QR Code
//           </div>
//           <div className="text-[10px] text-slate-500 leading-relaxed">
//             <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Payment TO</p>
//             <p className="font-bold text-slate-800">Shree Mobile Store</p>
//             <p>HDFC BANK LTD · ACC NO.: 502 000 624 338 73</p>
//             <p>IFSC CODE: HDFC0001452 · BRANCH: C.G ROAD CHEMBUR · ACC TYPE: CURRENT</p>
//           </div>
//         </div>

//         {/* ── SUMMARY VERIFICATION SECTION ── */}
//         <div className="mx-4 mb-4 rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white p-4">
//           <div className="flex items-start gap-2 mb-3">
//             <ShieldCheck size={15} className="text-indigo-500 mt-0.5 flex-shrink-0" />
//             <p className="text-[12px] font-semibold text-slate-700 leading-relaxed">
//               Do you agree with this summary? To verify, click on the{" "}
//               <span className="text-indigo-600 font-bold">Verify</span> button.
//             </p>
//           </div>
//           <button
//             onClick={openModal}
//             className="flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold text-white transition-all hover:scale-105 active:scale-95"
//             style={{
//               background: "linear-gradient(135deg, #4f46e5, #6366f1)",
//               boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
//             }}
//           >
//             <CheckCircle2 size={13} />
//             Verify Summary
//           </button>
//         </div>

//         {/* Footer */}
//         <div className="flex justify-end px-5 py-3 border-t border-slate-200">
//           <span className="font-black text-slate-900">
//             Go<span className="text-blue-500">electronix</span>
//           </span>
//         </div>
//       </div>

//       {/* ══════════════════════════════════════════════════════════════
//           VERIFICATION MODAL
//       ══════════════════════════════════════════════════════════════ */}
//       {showModal && (
//         <div
//           className="fixed inset-0 z-50 flex items-center justify-center"
//           style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
//           onClick={(e) => e.target === e.currentTarget && closeModal()}
//         >
//           <div
//             className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden"
//             style={{ animation: "slideUp 0.25s ease" }}
//           >
//             {/* ── Step: Invoice Number ── */}
//             {verifyStep === "invoice" && (
//               <div className="p-7">
//                 {/* Header */}
//                 <div className="flex items-start justify-between mb-5">
//                   <div>
//                     <h3 className="text-[15px] font-black text-slate-900">Verify Payout Summary</h3>
//                     <p className="text-[11px] text-slate-500 mt-0.5">This month's commission · Week 42–43 · 2025</p>
//                   </div>
//                   <button onClick={closeModal} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
//                     <X size={13} />
//                   </button>
//                 </div>

//                 {/* Step Pills */}
//                 <StepIndicator steps={steps} current={0} />

//                 {/* Summary Box */}
//                 <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-[10px] space-y-1.5">
//                   {[
//                     ["Period",         "Week 42–43 · Oct–Nov 2025"],
//                     ["Total Payouts",  "5 records"],
//                     ["Total Amount",   "₹75,500"],
//                     ["Transferred",    "₹37,000 (2 payouts)"],
//                     ["Pending",        "₹38,500 (3 payouts)"],
//                   ].map(([lbl, val]) => (
//                     <div key={lbl} className="flex justify-between">
//                       <span className="text-slate-500">{lbl}</span>
//                       <span className="font-bold text-slate-800">{val}</span>
//                     </div>
//                   ))}
//                 </div>

//                 {/* Invoice Input */}
//                 <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
//                   Enter Invoice Number
//                 </label>
//                 <input
//                   type="text"
//                   value={invoiceVal}
//                   onChange={(e) => { setInvoiceVal(e.target.value); setInvoiceError(""); }}
//                   placeholder="e.g. INV-9001-2025"
//                   className="w-full border rounded-xl px-3 py-2.5 text-[12px] font-mono text-slate-800 outline-none transition-all"
//                   style={{
//                     borderColor: invoiceError ? "#ef4444" : "#e2e8f0",
//                     boxShadow: invoiceError ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
//                   }}
//                   onFocus={(e) => !invoiceError && (e.target.style.borderColor = "#6366f1")}
//                   onBlur={(e) => !invoiceError && (e.target.style.borderColor = "#e2e8f0")}
//                   onKeyDown={(e) => e.key === "Enter" && handleInvoiceNext()}
//                 />
//                 {invoiceError && (
//                   <p className="flex items-center gap-1 text-[10px] text-red-500 mt-1.5">
//                     <AlertCircle size={10} /> {invoiceError}
//                   </p>
//                 )}

//                 <button
//                   onClick={handleInvoiceNext}
//                   className="w-full mt-4 py-2.5 rounded-xl text-[12px] font-bold text-white transition-all hover:opacity-90 active:scale-[0.98]"
//                   style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 10px rgba(99,102,241,0.35)" }}
//                 >
//                   Continue to Send OTP →
//                 </button>
//               </div>
//             )}

//             {/* ── Step: Send OTP ── */}
//             {verifyStep === "sendOtp" && (
//               <div className="p-7">
//                 <div className="flex items-start justify-between mb-5">
//                   <div>
//                     <h3 className="text-[15px] font-black text-slate-900">Send OTP to Email</h3>
//                     <p className="text-[11px] text-slate-500 mt-0.5">
//                       Invoice:{" "}
//                       <span className="text-indigo-600 font-bold">{invoiceVal}</span>
//                     </p>
//                   </div>
//                   <button onClick={closeModal} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
//                     <X size={13} />
//                   </button>
//                 </div>

//                 <StepIndicator steps={steps} current={1} />

//                 <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-[10px] space-y-1.5">
//                   {[
//                     ["Registered Email", "r***l@goelectronix.in"],
//                     ["OTP Valid For",    "5 minutes"],
//                   ].map(([lbl, val]) => (
//                     <div key={lbl} className="flex justify-between">
//                       <span className="text-slate-500">{lbl}</span>
//                       <span className="font-bold text-slate-800">{val}</span>
//                     </div>
//                   ))}
//                 </div>

//                 {isSending ? (
//                   <div className="flex items-center justify-center gap-3 py-4 text-[12px] font-semibold text-indigo-600">
//                     <div
//                       className="w-4 h-4 rounded-full border-2 border-indigo-200"
//                       style={{
//                         borderTopColor: "#6366f1",
//                         animation: "spin 0.7s linear infinite",
//                       }}
//                     />
//                     Sending OTP to your email...
//                   </div>
//                 ) : (
//                   <>
//                     <p className="text-[11px] text-slate-500 leading-relaxed mb-4">
//                       An OTP will be sent to your registered email address to verify this payout summary.
//                     </p>
//                     <button
//                       onClick={handleSendOtp}
//                       className="w-full py-2.5 rounded-xl text-[12px] font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
//                       style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 10px rgba(99,102,241,0.35)" }}
//                     >
//                       <Mail size={13} /> Send OTP via Email
//                     </button>
//                   </>
//                 )}
//               </div>
//             )}

//             {/* ── Step: Verify OTP ── */}
//             {verifyStep === "verifyOtp" && (
//               <div className="p-7">
//                 <div className="flex items-start justify-between mb-5">
//                   <div>
//                     <h3 className="text-[15px] font-black text-slate-900">Enter Verification OTP</h3>
//                     <p className="text-[11px] text-slate-500 mt-0.5">Sent to r***l@goelectronix.in</p>
//                   </div>
//                   <button onClick={closeModal} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
//                     <X size={13} />
//                   </button>
//                 </div>

//                 <StepIndicator steps={steps} current={2} />

//                 <p className="text-[11px] text-slate-500 mb-1 leading-relaxed">
//                   Enter the 6-digit OTP sent to your email.{" "}
//                   <span className="text-green-600 font-semibold">✓ OTP sent successfully.</span>
//                 </p>

//                 <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 mt-4">
//                   6-Digit OTP
//                 </label>
//                 <div className="flex gap-2 mb-1">
//                   {otpDigits.map((digit, i) => (
//                     <input
//                       key={i}
//                       ref={otpRefs[i]}
//                       type="text"
//                       inputMode="numeric"
//                       maxLength={1}
//                       value={digit}
//                       onChange={(e) => handleOtpInput(i, e.target.value)}
//                       onKeyDown={(e) => handleOtpKeydown(e, i)}
//                       className="w-11 h-12 text-center rounded-xl text-[18px] font-black font-mono outline-none border-2 transition-all"
//                       style={{
//                         borderColor: otpError ? "#ef4444" : digit ? "#6366f1" : "#e2e8f0",
//                         color: "#1e293b",
//                         boxShadow: digit && !otpError ? "0 0 0 3px rgba(99,102,241,0.1)" : "none",
//                       }}
//                     />
//                   ))}
//                 </div>
//                 {otpError && (
//                   <p className="flex items-center gap-1 text-[10px] text-red-500 mb-2">
//                     <AlertCircle size={10} /> {otpError}
//                   </p>
//                 )}

//                 <button
//                   onClick={handleVerifyOtp}
//                   className="w-full mt-4 py-2.5 rounded-xl text-[12px] font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
//                   style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 10px rgba(99,102,241,0.35)" }}
//                 >
//                   <ShieldCheck size={13} /> Verify & Confirm
//                 </button>

//                 <p className="text-center text-[10px] text-slate-400 mt-3">
//                   Didn't receive it?{" "}
//                   <button onClick={resendOtp} className="text-indigo-600 font-bold hover:underline">
//                     Resend OTP
//                   </button>
//                 </p>
//               </div>
//             )}

//             {/* ── Step: Success ── */}
//             {verifyStep === "success" && (
//               <div className="p-8 text-center">
//                 <div
//                   className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl"
//                   style={{ background: "#dcfce7" }}
//                 >
//                   ✅
//                 </div>
//                 <h3 className="text-[17px] font-black text-slate-900 mb-2">Summary Verified!</h3>
//                 <p className="text-[11px] text-slate-500 leading-relaxed mb-6">
//                   Payout summary for <strong className="text-slate-700">Week 42–43 · 2025</strong> has been
//                   successfully verified and marked as billed. Redirecting you to the Billed tab.
//                 </p>
//                 <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
//                   <div
//                     className="h-full rounded-full transition-all"
//                     style={{
//                       width: `${redirectPct}%`,
//                       background: "linear-gradient(90deg,#4f46e5,#6366f1)",
//                     }}
//                   />
//                 </div>
//                 <p className="text-[10px] text-slate-400">
//                   Redirecting in {redirectSecs}s...
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>
//       )}

//       {/* Keyframe styles */}
//       <style>{`
//         @keyframes slideUp {
//           from { opacity: 0; transform: translateY(20px); }
//           to   { opacity: 1; transform: translateY(0); }
//         }
//         @keyframes spin {
//           to { transform: rotate(360deg); }
//         }
//       `}</style>
//     </div>
//   );
// }

// // ── Step Indicator atom ───────────────────────────────────────────────────────
// function StepIndicator({ steps, current }) {
//   return (
//     <div className="flex items-center mb-6">
//       {steps.map((label, i) => {
//         const done   = i < current;
//         const active = i === current;
//         return (
//           <div key={label} className="flex items-center flex-1 last:flex-none">
//             <div className="flex items-center gap-1.5">
//               <div
//                 className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0"
//                 style={{
//                   background: done   ? "#dcfce7"
//                             : active ? "#4f46e5"
//                             : "#f1f5f9",
//                   color:      done   ? "#16a34a"
//                             : active ? "#ffffff"
//                             : "#94a3b8",
//                   border: done   ? "1.5px solid #86efac"
//                         : active ? "none"
//                         : "1.5px solid #e2e8f0",
//                   boxShadow: active ? "0 2px 8px rgba(79,70,229,0.3)" : "none",
//                 }}
//               >
//                 {done ? "✓" : i + 1}
//               </div>
//               <span
//                 className="text-[10px] font-bold whitespace-nowrap"
//                 style={{ color: active ? "#4f46e5" : done ? "#16a34a" : "#94a3b8" }}
//               >
//                 {label}
//               </span>
//             </div>
//             {i < steps.length - 1 && (
//               <div
//                 className="flex-1 h-0.5 mx-2 rounded-full"
//                 style={{ background: done ? "#86efac" : "#e2e8f0" }}
//               />
//             )}
//           </div>
//         );
//       })}
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  ROOT: CommissionsView
// // ─────────────────────────────────────────────────────────────────────────────
// export default function CommissionsView() {
//   const [payoutList, setPayoutList] = useState(commissionPayouts);
//   const [activeTab, setActiveTab]   = useState("current"); // "billed" | "unbilled"
//   const [typeFilter, setTypeFilter] = useState("ALL");
//   const [selectedSummary, setSelectedSummary] = useState(null); // New State

//   const FILTERS = ["ALL", "MCP", "CP", "Promoter"];

//   const Navigate = useNavigate();
  
//   // ── Split lists ────────────────────────────────────────────────────────────
//   const billedList   = useMemo(() => payoutList.filter(p => p.transferred),  [payoutList]);
//   const unbilledList = useMemo(() => payoutList.filter(p => !p.transferred), [payoutList]);
//   const activeList   = activeTab === "billed" ? billedList : unbilledList;

//   // ── Monthly filter helper ──────────────────────────────────────────────────
//   const isThisMonth = (p) => p.date?.startsWith(currentMonth);

//   // ── Billed stats ───────────────────────────────────────────────────────────
//   const totalBilled        = billedList.reduce((s, p) => s + p.payableAmount, 0);
//   const monthlyBilled      = billedList.filter(isThisMonth).reduce((s, p) => s + p.payableAmount, 0);
//   const totalBilledCount   = billedList.length;

//   // ── Unbilled stats ─────────────────────────────────────────────────────────
//   const totalUnbilled      = unbilledList.reduce((s, p) => s + p.payableAmount, 0);
//   const monthlyUnbilled    = unbilledList.filter(isThisMonth).reduce((s, p) => s + p.payableAmount, 0);
//   const totalUnbilledCount = unbilledList.length;

//   // ── Toggle handler ─────────────────────────────────────────────────────────
//   const handleToggle = (id) => {
//     setPayoutList(prev => prev.map(p =>
//       p.id === id
//         ? {
//             ...p,
//             transferred: !p.transferred,
//             date:  p.transferred ? p.date : todayISO(),
//             txnId: p.transferred ? p.txnId : (p.txnId === "-" ? `MANUAL-${id}` : p.txnId),
//           }
//         : p
//     ));
//   };

//   // ── Card configs ───────────────────────────────────────────────────────────
//   const billedCards = [
//     {
//       label:    "Total Billed",
//       value:    `₹${formatCurrency(totalBilled)}`,
//       sublabel: `${totalBilledCount} payouts transferred`,
//       icon:     CheckCircle2,
//       accent:   "#059669",
//       bg:       "#ECFDF5",
//     },
//     {
//       label:    "Monthly Total Bill",
//       value:    `₹${formatCurrency(monthlyBilled)}`,
//       sublabel: `Transferred this month`,
//       icon:     Calendar,
//       accent:   "#2563EB",
//       bg:       "#EFF6FF",
//     },
//     {
//       label:    "Avg. Per Payout",
//       value:    `₹${formatCurrency(totalBilledCount ? Math.round(totalBilled / totalBilledCount) : 0)}`,
//       sublabel: "Average transferred amount",
//       icon:     TrendingUp,
//       accent:   "#7C3AED",
//       bg:       "#F5F3FF",
//     },
//   ];

//   const unbilledCards = [
//     {
//       label:    "Total Unbill",
//       value:    `₹${formatCurrency(totalUnbilled)}`,
//       sublabel: `${totalUnbilledCount} payouts pending`,
//       icon:     Clock,
//       accent:   "#D97706",
//       bg:       "#FFFBEB",
//     },
//     {
//       label:    "Monthly Total Unbill",
//       value:    `₹${formatCurrency(monthlyUnbilled)}`,
//       sublabel: "Pending this month",
//       icon:     Calendar,
//       accent:   "#EF4444",
//       bg:       "#FEF2F2",
//     },
//     {
//       label:    "Avg. Per Payout",
//       value:    `₹${formatCurrency(totalUnbilledCount ? Math.round(totalUnbilled / totalUnbilledCount) : 0)}`,
//       sublabel: "Average pending amount",
//       icon:     AlertCircle,
//       accent:   "#0891B2",
//       bg:       "#ECFEFF",
//     },
//   ];

  

//   const activeCards = activeTab === "billed" ? billedCards : "unbilled" ? unbilledCards : "current" ? unbilledCards : "";

//   if (selectedSummary) {
//     return <PayoutSummary data={selectedSummary} onBack={() => setSelectedSummary(null)} />;
//   }

//   return (
//     <div className="space-y-5">

//       {/* ── TAB BAR ── */}
//       <div
//         className="flex items-center"
//         style={{ borderBottom:"2px solid #F1F5F9" }}
//       >
//         <TabButton
//           label="Current Payout"
//           active={activeTab === "current"}
//           count={billedList.length}
//           onClick={() => setActiveTab("current")}
//           accent="#059669"
//         />
//         <TabButton
//           label="Unbilled"
//           active={activeTab === "unbilled"}
//           count={unbilledList.length}
//           onClick={() => setActiveTab("unbilled")}
//           accent="#D97706"
//         />
//         <TabButton
//           label="Billed"
//           active={activeTab === "billed"}
//           count={billedList.length}
//           onClick={() => setActiveTab("billed")}
//           accent="#059669"
//         />
        
        
//       </div>

//       {/* ── KPI CARDS ── */}
//       <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
//         {activeCards.map((card, i) => (
//           <StatCard key={card.label} {...card} index={i}/>
//         ))}
//       </div>

//       {/* ── TABLE ── */}
//       <SectionCard
//         title={activeTab === "billed" ? "Billed Payouts Register" : "unbill" ? "Unbilled Payouts Register" : "current" ? "current month" : ""}
//         right={
//           <div className="flex justify-end gap-3 mb-5">
//           <div className="flex gap-2 flex-wrap">
//               {FILTERS.map((item) => (
//                 <button
//                   key={item}
//                   onClick={() => setTypeFilter(item)}
//                   className="px-4 py-1.5 rounded-full text-xs font-bold transition-all"
//                   style={{
//                     background:
//                       typeFilter === item ? "#2563EB" : "#F1F5F9",
//                     color:
//                       typeFilter === item ? "#FFFFFF" : "#64748B",
//                     border:
//                       typeFilter === item
//                         ? "1px solid #2563EB"
//                         : "1px solid #E2E8F0",
//                   }}
//                 >
//                   {item}
//                 </button>
//               ))}
//             </div>

//             <SearchInput />

//           <div>
//             <span
//               className="text-[11px] font-bold px-2.5 py-1 rounded-full"
//               style={{
//                 background: activeTab === "billed" ? "#ECFDF5" : "#FFFBEB",
//                 color:      activeTab === "billed" ? "#059669" : "#D97706",
//               }}
//             >
//               {activeList.length} records
//             </span>
//           </div>
//           </div>
//         }
//       >
//         <CommissionTable list={activeList} onToggle={handleToggle} activeTab={activeTab} Navigate={Navigate} setSelectedSummary={setSelectedSummary}/>
//       </SectionCard>
//     </div>
//   );
// }


// import { Badge, CheckCircle2, IndianRupee } from "lucide-react";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { useState } from "react";


// const commissionPayouts = [
//   {
//     id: "PAY-9001",
//     forType: "Agent",
//     entityName: "Rahul Patil",
//     period: "Week 42 · 2025",
//     role: "Onboarding Commission",
//     commissionAmount: 22000,
//     payableAmount: 20000,
//     transferred: true,
//     bankName: "HDFC Bank",
//     accountLast4: "1234",
//     txnId: "TXN-HDFC-2025-001",
//     date: "2025-10-25",
//   },
//   {
//     id: "PAY-9002",
//     forType: "Retailer",
//     entityName: "Shree Mobile Store",
//     period: "Week 42 · 2025",
//     role: "Warranty Sales Payout",
//     commissionAmount: 12000,
//     payableAmount: 12000,
//     transferred: false,
//     bankName: "ICICI Bank",
//     accountLast4: "5678",
//     txnId: "-",
//     date: "2025-10-27",
//   },
//   {
//     id: "PAY-9003",
//     forType: "Agent",
//     entityName: "Priya Singh",
//     period: "Week 41 · 2025",
//     role: "Onboarding Commission",
//     commissionAmount: 15000,
//     payableAmount: 15000,
//     transferred: true,
//     bankName: "SBI",
//     accountLast4: "9999",
//     txnId: "TXN-SBI-2025-221",
//     date: "2025-10-20",
//   },
// ];


// const todayISO = () => new Date().toISOString().slice(0, 10);

// export default function CommissionsView() {

//   const [payoutList, setPayoutList] = useState(commissionPayouts);
//   const totalCommission = payoutList.reduce((sum, row) => sum + (row.commissionAmount || 0), 0);
//   const totalPayable = payoutList.reduce((sum, row) => sum + (row.payableAmount || 0), 0);
//   const totalTransferred = payoutList
//     .filter((row) => row.transferred)
//     .reduce((sum, row) => sum + (row.payableAmount || 0), 0);

//   const handleMarkPayoutTransferred = (id) => {
//     setPayoutList((prev) =>
//       prev.map((p) =>
//         p.id === id
//           ? {
//               ...p,
//               transferred: !p.transferred,
//               date: todayISO(),
//               txnId: p.transferred ? p.txnId : p.txnId === "-" ? `MANUAL-${id}` : p.txnId,
//             }
//           : p
//       )
//     );
//   };

//   return (
//     <div className="space-y-4">
//       <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
//         <KpiCard
//           label="Total Unbilled"
//           value={`₹ ${formatCurrency(totalCommission)}`}
//           subLabel="Agent + Retailer"
//           icon={IndianRupee}
//         />
//         <KpiCard
//           label="Monthly Total unbill"
//           value={`₹ ${formatCurrency(totalPayable)}`}
//           subLabel="Approved to be paid"
//           icon={IndianRupee}
//         />
//         <KpiCard
//           label="Transferred"
//           value={`₹ ${formatCurrency(totalTransferred)}`}
//           subLabel=""
//           icon={CheckCircle2}
//         />
//       </div>

//       <SectionCard title="Commission Payout Register">
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Payout</th>
//                 <th className="py-2 text-left">Entity</th>
//                 <th className="py-2 text-left">Role / Period</th>
//                 <th className="py-2 text-left">Amounts (₹)</th>
//                 <th className="py-2 text-left">Bank / Txn</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Admin Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {payoutList.map((p) => (
//                 <tr
//                   key={p.id}
//                   className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                 >
//                   <td className="py-1.5 text-xs text-slate-700 font-medium">{p.id}</td>
//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div className="font-semibold">{p.entityName}</div>
//                     <div className="text-[10px] text-slate-500">{p.forType}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>{p.role}</div>
//                     <div className="text-[10px] text-slate-500">{p.period}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>
//                       Commission: ₹ {formatCurrency(p.commissionAmount || 0)}
//                     </div>
//                     <div className="text-[10px] text-slate-500">
//                       Payable: ₹ {formatCurrency(p.payableAmount || 0)}
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>
//                       {p.bankName} · ****{p.accountLast4}
//                     </div>
//                     <div className="text-[10px] text-slate-500">Txn: {p.txnId}</div>
//                     <div className="text-[10px] text-slate-500">Date: {p.date}</div>
//                   </td>
//                   <td className="py-1.5 text-xs">
//                     <Badge color={p.transferred ? "green" : "yellow"}>
//                       {p.transferred ? "Transferred" : "Pending"}
//                     </Badge>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <button
//                       onClick={() => handleMarkPayoutTransferred(p.id)}
//                       className="text-[10px] px-2 py-0.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
//                     >
//                       {p.transferred ? "Mark as pending" : "Mark as transferred"}
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="Admin Notes – Weekly Combined Statements">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             Commissions from agent dashboard and retailer dashboard are combined
//             into weekly statements.
//           </li>
//           <li>
//             Each row records whether the amount is actually transferred, with bank
//             name, account last digits, transaction id and date.
//           </li>
//           <li>
//             Export to Excel/PDF for accounting and reconciliation with finance.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }